import React, { useCallback, useMemo } from 'react';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { ProgressOutput } from 'libs/end-to-end/src/progress';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { getDateAsYYYYMMDD } from 'libs/end-to-end/src/dates';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { DownloadButton } from 'apps/sigma2-ui/src/components/ui/DownloadButton';
import { IconDownloadAnalysisSvg } from 'apps/sigma2-ui/src/components/svg/IconDownloadAnalysis';
import { useAnalytics } from 'apps/sigma2-ui/src/lib/hooks/useAnalytics';
import { ProgressExplanationSerializableProps } from 'libs/end-to-end/src/progress-explanation';
import { constructProgressExplanationCopy } from 'libs/end-to-end/src/progress-explanation-utils';
import { ProgressExplanationAttributes } from 'libs/end-to-end/src/progress-explanation';
import { getProgressPageExplanationAttributes } from 'libs/end-to-end/src/progress-explanation-utils';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { convertTranslatedContentToReactNode } from 'apps/sigma2-ui/src/lib/hooks/useTranslation';

type ProgressExplanationProps = ProgressExplanationSerializableProps & {
  downloadContainerRef: React.RefObject<HTMLDivElement> | null;
};

const useExplanationAttributes = (
  data: ProgressOutput,
  myObservation: MyObservation | null,
  comparisonGroup: string,
): ProgressExplanationAttributes => {
  return useMemo(() => {
    return getProgressPageExplanationAttributes({
      data,
      myObservation,
      comparisonGroup,
    });
  }, [data, myObservation, comparisonGroup]);
};

export const ProgressExplanation = (props: ProgressExplanationProps) => {
  const explanationProps = useExplanationAttributes(
    props.data,
    props.myObservation,
    props.comparisonGroup,
  );

  const copy = useMemo(() => {
    return (
      <p>
        {constructProgressExplanationCopy({
          ...props,
          ...explanationProps,
        })}
      </p>
    );
  }, [explanationProps, props]);

  const language = useLanguage();

  const { downloadContainerRef } = props;
  const getElementToDownload = useCallback(
    // this callback is only used if downloadContainerRef is not null so we
    // can safely assert it is not null at this point
    () => downloadContainerRef!.current,
    [downloadContainerRef],
  );

  const downloadName = useMemo(
    () =>
      `Trends-${explanationProps.metricDisplay}-${explanationProps.dimensionDisplay}-${getDateAsYYYYMMDD(new Date())}`,
    [explanationProps],
  );

  const analytics = useAnalytics();

  const onDownloadCb = useCallback(() => {
    analytics.trackEvent({
      eventName: 'Chart Downloaded',
      eventProperties: {
        type: 'Progress Page',
      },
    });
  }, [analytics]);

  return (
    <Box display="flex" flexDirection="col" p="sm">
      <div className="p-2 text-xs leading-6 text-gray-900">{copy}</div>

      {downloadContainerRef !== null && (
        <div className="p-2 text-xs leading-6 font-bold text-royal-800">
          <DownloadButton
            getElement={getElementToDownload}
            filename={downloadName}
            onDownloadCb={onDownloadCb}
          >
            <Spacing direction="horizontal" size="xs" className="items-center">
              <IconDownloadAnalysisSvg />
              <span>{convertTranslatedContentToReactNode(language.downloadAnalysis())}</span>
            </Spacing>
          </DownloadButton>
        </div>
      )}
    </Box>
  );
};
